import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React from 'react';

// Initialize dayjs plugins globally
dayjs.extend(relativeTime);

import {
  AvatarDropdown,
  ErrorBoundary,
  Footer,
  LangDropdown,
  OfflineBanner,
} from '@/components';
import WecomSensitiveAuthBootstrap from '@/components/WecomSensitiveAuthBootstrap';
import AccessHeartbeat from '@/components/AccessHeartbeat';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { clearToken, getToken } from '@/services/auth/wecom';
import { redirectToLogin } from '@/utils/authSession';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const isAuthFreePath = (pathname: string) =>
  pathname === loginPath ||
  pathname.startsWith('/user/') ||
  pathname.startsWith('/public/');

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  settingDrawerOpen?: boolean;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      const user = (msg as any)?.data ?? msg;
      if (user && (user.userid || user.name)) {
        return user as API.CurrentUser;
      }
      return undefined;
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401) {
        clearToken();
      }
      return undefined;
    }
  };
  // 如果不是登录/公开页，执行
  const { location } = history;
  if (!isAuthFreePath(location.pathname)) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      if (getToken()) {
        // 有 token 但拉不到用户（多为后端暂不可用），保留 token 避免误踢
        return {
          fetchUserInfo,
          settings: defaultSettings as Partial<LayoutSettings>,
          settingDrawerOpen: false,
        };
      }
      redirectToLogin();
      return {
        fetchUserInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
        settingDrawerOpen: false,
      };
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      settingDrawerOpen: false,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    settingDrawerOpen: false,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    menuItemRender: (item, dom) => {
      if (item.path) {
        return (
          <Link to={item.path} prefetch>
            {dom}
          </Link>
        );
      }
      return dom;
    },
    actionsRender: () => {
      // `locale: false` opts out of the language switcher. ProLayout's own
      // `locale` prop is a locale string, so narrow to the boolean toggle here.
      const localeEnabled =
        (initialState?.settings as { locale?: boolean })?.locale !== false;
      return [localeEnabled && <LangDropdown key="lang" />].filter(Boolean);
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: initialState?.currentUser?.name || 'ERP 用户',
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (isAuthFreePath(location.pathname)) {
        return;
      }
      // 已登录
      if (initialState?.currentUser) return;

      if (getToken()) {
        clearToken();
      }
      history.replace(
        `${loginPath}?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`,
      );
    },
    bgLayoutImgList: [],
    links: [
      <a key="deroad" href="https://deroad.top" target="_blank" rel="noreferrer">
        Deroad（德路）
      </a>,
    ],
    // Replace ProLayout's default ErrorBoundary with our offline-aware version,
    // so chunk load errors show friendly messages instead of "Something went wrong."
    ErrorBoundary,
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      const hideSettingDrawer =
        typeof window !== 'undefined' && window.innerWidth < 768;
      return (
        <>
          <WecomSensitiveAuthBootstrap />
          <AccessHeartbeat />
          {children}
          {!hideSettingDrawer && isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              collapse={initialState?.settingDrawerOpen}
              onCollapseChange={(open) => {
                setInitialState((s) => ({
                  ...s,
                  settingDrawerOpen: open,
                }));
              }}
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((s) => ({
                  ...s,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 *
 * 生产环境走同域相对路径 `/api/*`，由 Nginx 反代到 Nest；
 * 勿再指向 Ant Design Pro 演示 API（会导致跨域 CORS）。
 * 如需直连其它域名，构建时设置 `API_BASE_URL`。
 */
export const request: RequestConfig = {
  baseURL: process.env.API_BASE_URL || '',
  ...errorConfig,
};

export function rootContainer(container: React.ReactNode) {
  return (
    <>
      <OfflineBanner />
      <ErrorBoundary>{container}</ErrorBoundary>
    </>
  );
}
